(ns ui.view.counterparty-diligence.send-to-responders-modal.view
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.util :as cu]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.components.radix-ui.avatar.view :as avatar]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.util :as uu]
   [ui.view.counterparty-diligence.send-to-responders-modal.event :as event]
   [ui.view.counterparty-diligence.send-to-responders-modal.sub :as sub]
   [frontend.view.entity :as f-entity]))

(defn responder-table
  [{:keys [responders remove-responders]}]
  (let [responder-count (count responders)
        removing-responders @(rf/subscribe [::sub/removing-responders])]
    [:div
     {:class "rounded overflow-hidden w-full border"}
     [:table
      {:class "border-collapse w-full"}
      [:thead]
      [:tbody
       (->> responders
            (map-indexed
             (fn [idx responder]
               (let [id (:db/id responder)
                     initials (entity/get-initials responder)
                     last? (= idx (dec responder-count))
                     removing? (contains? removing-responders id)]
                 ^{:key (str id)}
                 [:tr
                  [:td
                   {:class (uu/cm "px-3 py-2"
                                  (when-not last?
                                    "border border-x-0 border-t-0"))}
                   [:div
                    {:class "flex items-center gap-3"}
                    [avatar/view
                     {:image-url (:account/picture-url responder)
                      :size      :small
                      :fallback  (or initials
                                     [:i.fal.fa-user])}]
                    [:div
                     [:p (entity/display-string responder)]
                     [:p
                      {:class "text-sm m-0 text-base-light-text-paler"}
                      (or (:person/universal-email-address responder)
                          (:account/email responder))]]]]

                  [:td
                   {:class (uu/cm "px-3 py-2"
                                  (when-not last?
                                    "border border-x-0 border-t-0"))}
                   [:div
                    {:class "flex justify-end items-center h-full"}
                    [button/sm-secondary-hollow
                     {:disabled? removing?
                      :on-click  #(remove-responders [id])}
                     (loc/tr (if removing?
                               :removing-ellipsis
                               :remove))]]]]))))]]]))

(defn modal-header
  []
  (let [new-responders @(rf/subscribe [::sub/new-responders])]
    (if (seq new-responders)
      [:div
       {:class "flex gap-2 items-center"}
       [button/square-secondary-hollow
        {:tooltip  (loc/tr :cancel)
         :on-click #(rf/dispatch [::event/set-new-responders []])}
        [:i {:class "fal fa-arrow-left"}]]
       [:p
        {:class "text-lg text-medium"}
        (if (= (count new-responders) 1)
          (loc/tr :send-to- [(-> new-responders first :label)])
          (loc/tr :send-to-n-responders [(count new-responders)]))]]

      [:p
       {:class "text-lg text-medium"}
       (loc/tr :add-and-invite-responders)])))

(defn add-responder-input
  [create-responder-modal-open?*]
  (let [new-responders @(rf/subscribe [::sub/new-responders])]
    [labelled-input/view
     {:label              (loc/tr :add-responder)
      :editor-type        :editor.type/search
      :data-type          :data.type/ref-multi
      :field/entity-types [:entity.type/account :entity.type/person]
      :value              (when (seq new-responders)
                            new-responders)
      :on-change          (fn [_value options]
                            (rf/dispatch-sync [::event/set-new-responders options]))
      :create! (fn [initial-value]
                 (reset! create-responder-modal-open?* true)
                 (f-entity/open-creator!
                  :entity.type/person
                  {:start-value initial-value
                   :skip-success-msg? true
                   :creation-pipeline-id nil
                   :on-cancel! (fn []
                                 (reset! create-responder-modal-open?* false))
                   :on-close! (fn []
                                (reset! create-responder-modal-open?* false))
                   :on-ok! (fn [{:keys [new-entity close-cb!] :as _args}]
                             (let [new-opt {:value (:db/id new-entity)
                                            :label (entity/display-string new-entity)}
                                   new-responders (conj (vec new-responders) new-opt)]
                               (rf/dispatch-sync [::event/set-new-responders new-responders])
                               (reset! create-responder-modal-open?* false)
                               (close-cb!)))}))}]))

(defn send-invites-view
  [{:keys [submit close-modal]}]
  (let [submitting? @(rf/subscribe [::sub/submitting?])
        email-subject @(rf/subscribe [::sub/email-subject])
        email-body @(rf/subscribe [::sub/email-body])
        new-responders @(rf/subscribe [::sub/new-responders])
        can-submit? (and (not submitting?)
                         (seq new-responders)
                         (not (cu/blank-value? email-subject))
                         (not (cu/blank-value? email-body)))]
    [:div
     {:class "flex flex-col gap-5"}
     [labelled-input/view
      {:label     (loc/tr :email-subject)
       :data-type :data.type/text-simple
       :value     email-subject
       :on-change #(rf/dispatch-sync [::event/set-email-subject %])}]
     [labelled-input/view
      {:label     (loc/tr :email-body)
       :data-type :data.type/text-multiline
       :value     email-body
       :on-change #(rf/dispatch-sync [::event/set-email-body %])}]

     [:div
      {:class "flex justify-end items-center gap-2"}
      [button/sm-secondary-hollow
       {:on-click  close-modal
        :disabled? submitting?}
       (loc/tr :cancel)]
      [button/sm-primary-solid
       {:disabled? (not can-submit?)
        :on-click  submit}
       (cond
         submitting?
         (loc/tr :sending)
         (< 1 (count new-responders))
         (loc/tr :send-invites)
         :else (loc/tr :send-invite))]]]))

(defn view
  [_]
  (let [close-modal #(rf/dispatch [::event/clear-state-and-close-modal])
        create-responder-modal-open?* (r/atom false)]
    (r/create-class
     {:component-will-unmount (fn [_]
                                (close-modal))
      :reagent-render
      (fn [{:keys [process counterparty-diligence responders on-success-event refetch-data-event]}]
        (let [modal-open? @(rf/subscribe [::sub/modal-open?])
              new-responders @(rf/subscribe [::sub/new-responders])
              submit #(rf/dispatch [::event/add-and-invite-responders
                                    {:process                process
                                     :counterparty-diligence counterparty-diligence
                                     :on-success-event       on-success-event}])
              remove-responders (fn [ids]
                                  (rf/dispatch [::event/remove-responders
                                                {:process                 process
                                                 :responder-ids-to-remove ids
                                                 :counterparty-diligence  counterparty-diligence
                                                 :refetch-data-event      refetch-data-event}]))]
          [:<>
           [button/sm-primary-solid
            {:disabled? (not process)
             :on-click  #(rf/dispatch [::event/open-modal {:counterparty-diligence counterparty-diligence}])}
            [:i {:class "fal fa-user"}]
            (str (loc/tr :responders) " (" (count responders) ")")]

           [modal/view
            {:visible? (and modal-open? (not @create-responder-modal-open?*))
             :hide!    close-modal}
            [modal-header]
            [:div
             {:class "min-h-36 flex flex-col gap-5"}
             [add-responder-input create-responder-modal-open?*]
             (if (seq new-responders)
               [send-invites-view
                {:submit      submit
                 :close-modal close-modal}]
               [:div
                {:class "flex flex-col gap-1"}
                [:p
                 (loc/tr :responders)]
                (if (seq responders)
                  [responder-table
                   {:responders        responders
                    :remove-responders remove-responders}]
                  [:p
                   {:class "text-base-light-text-paler"}
                   (loc/tr :no-responders)])])]]]))})))
